import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { api } from '../lib/api';
import type { PosProduct } from '../lib/types';
import {
  SCAN_TIMEOUT_MS,
  classifyLookupError,
  createScanCoordinator,
  type LookupResult,
  type ScanCoordinator,
  type ScanOutcome,
} from '../lib/scan-coordinator';
import { Icon } from './icons';
import { Kbd } from './ui';
import { useT } from '../i18n';

/**
 * The scan bar.
 *
 * This is the most important control on the till, so it is built around not losing focus.
 * It refocuses after every scan, after every rejection, after any click on the page that is
 * not another field, and when a dialog closes (see `Modal`). It never steals focus while a
 * dialog is open or while the cashier is typing in another field, and keyboard users tabbing
 * across buttons are left alone.
 *
 * On a touch screen the same refocusing must not raise the on-screen keyboard over the items
 * every time a chip or tile is tapped. There the box is focused with `inputmode="none"`: a
 * Bluetooth scanner still types into it, but the soft keyboard opens only when the cashier taps
 * the box itself, and closes again when they leave it.
 *
 * It also takes words. A scanner types digits and presses Enter, and that is a barcode lookup
 * exactly as before. Anything the catalogue does not know as a barcode — "tomato", "2 kg onion",
 * "₹50 coriander" — goes to `onText`, which opens the search palette with it, so the cashier never
 * has to move to a different box to find an item by name.
 */

export interface ScanResult {
  product: PosProduct | null;
  /** what the scanner read, so the cashier can see what failed */
  scanned: string;
  /**
   * Set when the lookup did not answer normally. Readers that ignore it keep the legacy
   * not-found-shaped behavior; new UI branches on it for distinct failure messages.
   */
  error?: 'timeout' | 'offline' | 'auth-expired' | 'forbidden' | 'server-error' | 'queue-overflow';
}

interface Props {
  onScan: (result: ScanResult) => void;
  /** Text that is not a known barcode. Without it, everything is looked up as a barcode. */
  onText?: (text: string) => void;
  /**
   * Distinct scan-pipeline failures (timeout/offline/auth/forbidden/server/overflow).
   * Without it, failures degrade to the legacy not-found-shaped onScan with `error` set.
   */
  onScanError?: (info: {
    kind: NonNullable<ScanResult['error']>;
    scanned: string;
    message: string;
  }) => void;
  /**
   * Lets the bill owner drive scan generations (new bill / hold / resume / sign-out) so
   * late lookups cannot leak into another bill. See createScanCoordinator().newGeneration().
   */
  scannerHandle?: { current: ScanCoordinator<PosProduct> | null };
  disabled?: boolean;
  placeholder?: string;
}

const TYPING = new Set(['INPUT', 'TEXTAREA', 'SELECT']);
// ASCII and Tamil digits both count as "digits" for the barcode-vs-search decision.
const DIGITS = /^[0-9௦-௯]+$/;

/** A phone or tablet: no hover and a finger for a pointer. */
function isTouchScreen(): boolean {
  try {
    return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  } catch {
    return false;
  }
}

export function BarcodeInput({ onScan, onText, onScanError, scannerHandle, disabled = false, placeholder: placeholderProp }: Props) {
  const t = useT();
  const placeholder = placeholderProp ?? t('scan.placeholder');
  const [value, setValue] = useState('');
  const [looking, setLooking] = useState(false);
  const [touch] = useState(isTouchScreen);
  /** True while the cashier deliberately typing on a touch screen (they tapped the box). */
  const [typing, setTyping] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // The coordinator outlives any single render's closures, so it always reads callbacks here.
  const callbacksRef = useRef({ onScan, onText, onScanError });
  callbacksRef.current = { onScan, onText, onScanError };
  const coordinatorRef = useRef<ScanCoordinator<PosProduct> | null>(null);
  if (coordinatorRef.current === null) {
    coordinatorRef.current = createScanCoordinator<PosProduct>({
      lookup: lookupBarcode,
      onOutcome: handleScanOutcome,
    });
    if (scannerHandle) scannerHandle.current = coordinatorRef.current;
  }

  useEffect(() => {
    if (disabled) return;

    function modalOpen() {
      return document.querySelector('[aria-modal="true"]') !== null;
    }

    function refocus() {
      if (document.visibilityState !== 'visible' || modalOpen()) return;
      const active = document.activeElement;
      if (active instanceof HTMLElement && TYPING.has(active.tagName)) return;
      inputRef.current?.focus();
    }

    // Focus fell on the page body (a clicked label, a removed button): put it back.
    function onFocusOut() {
      window.setTimeout(() => {
        if (document.activeElement === document.body) refocus();
      }, 0);
    }

    // After any pointer click on a button or blank area, the scanner must be ready again.
    function onClick(event: MouseEvent) {
      const target = event.target;
      if (target instanceof HTMLElement && (TYPING.has(target.tagName) || target.closest('[aria-modal="true"]'))) return;
      window.setTimeout(refocus, 0);
    }

    document.addEventListener('focusout', onFocusOut);
    document.addEventListener('visibilitychange', refocus);
    document.addEventListener('click', onClick);
    inputRef.current?.focus();
    return () => {
      document.removeEventListener('focusout', onFocusOut);
      document.removeEventListener('visibilitychange', refocus);
      document.removeEventListener('click', onClick);
    };
  }, [disabled]);

  // Late outcomes must never reach an unmounted input (close, navigation, logout).
  useEffect(() => {
    // StrictMode mounts, unmounts and remounts every component in development. The cleanup
    // below disposes the coordinator; on the remount the same dead coordinator would still
    // sit in the ref and silently swallow every scan (ingest returns immediately on a
    // disposed coordinator). Recreate it here when that happens.
    let coord = coordinatorRef.current;
    if (!coord || coord.disposed) {
      coord = createScanCoordinator<PosProduct>({
        lookup: lookupBarcode,
        onOutcome: handleScanOutcome,
      });
      coordinatorRef.current = coord;
      if (scannerHandle) scannerHandle.current = coord;
    }
    return () => {
      coord?.dispose();
    };
  }, []);

  function submit() {
    const raw = value;
    const coord = coordinatorRef.current;
    setValue('');
    // A bare Enter on the cleared field is double-Enter noise, not a scan: no event is
    // ingested, so a double Enter can never double-add. Deliberate repeat scans arrive as
    // separate submits with their own text and become separate events (separate units).
    if (raw.trim() === '' || !coord) {
      if (coord && coord.pendingCount > 0) setLooking(true);
      inputRef.current?.focus();
      return;
    }
    setLooking(true);
    coord.ingest({ raw, source: 'hardware' });
  }

  async function lookupBarcode(code: string): Promise<LookupResult<PosProduct>> {
    try {
      const result = await api.get<{ found: boolean; product: PosProduct | null }>(
        '/api/pos/products/lookup',
        { barcode: code },
        AbortSignal.timeout(SCAN_TIMEOUT_MS),
      );
      if (result.found && result.product) return { kind: 'found', product: result.product };
      return { kind: 'not-found' };
    } catch (error) {
      // Classified, never collapsed: a network failure stays distinct from a barcode miss.
      return { kind: classifyLookupError(error) };
    }
  }

  function handleScanOutcome(outcome: ScanOutcome<PosProduct>) {
    const { onScan, onText, onScanError } = callbacksRef.current;
    const coord = coordinatorRef.current;
    // The lookup ran with the trimmed code (leading zeros preserved); ScanResult.scanned
    // keeps the legacy trimmed shape while the exact raw stays on the outcome event.
    const scanned = outcome.lookupCode;
    switch (outcome.kind) {
      case 'added':
        onScan({ product: outcome.product ?? null, scanned });
        break;
      case 'not-found':
        // Words that are not a barcode are a search: hand them over instead of reporting "no
        // product carries barcode tomato". Digits stay a barcode miss, which the till reports.
        if (onText && !DIGITS.test(scanned)) onText(scanned);
        else onScan({ product: null, scanned });
        break;
      case 'timeout':
      case 'offline':
      case 'auth-expired':
      case 'forbidden':
      case 'server-error':
      case 'queue-overflow':
        // Legacy word routing is preserved: non-digit text still opens search even when the
        // lookup transport failed, exactly as before — the failure is additionally signaled.
        if (onText && !DIGITS.test(scanned)) onText(scanned);
        else onScan({ product: null, scanned, error: outcome.kind });
        if (onScanError) onScanError({ kind: outcome.kind, scanned, message: outcome.message });
        break;
      case 'duplicate-suppressed':
      case 'stale-generation':
      case 'camera-repeat-suppressed':
        // Non-events by design: no double-add, no cross-bill leak, no second unit. Silent.
        break;
    }
    if (coord && coord.pendingCount === 0) {
      setLooking(false);
      inputRef.current?.focus();
    }
  }

  return (
    <div className="scanbar h-16 short:h-12" data-disabled={disabled ? 'true' : undefined}>
      <span
        aria-hidden
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl short:h-8 short:w-8"
        style={{ background: 'var(--ui-primary-soft)', color: 'var(--ui-primary)' }}
      >
        <Icon name="barcode" size={22} strokeWidth={2} />
      </span>
      <input
        ref={inputRef}
        className="h-full text-lg font-semibold text-slate-900 placeholder:font-normal placeholder:text-slate-400 short:text-base"
        data-testid="pos-barcode-input"
        // The till's F-keys must work from here: this box has focus nearly all day, and a scanner
        // only ever types characters and Enter, so no scan can trigger a shortcut.
        data-shortcuts="allow"
        value={value}
        disabled={disabled}
        placeholder={disabled ? t('scan.placeholderNoShift') : placeholder}
        aria-label={t('scan.input')}
        autoComplete="off"
        autoCapitalize="off"
        spellCheck={false}
        // Focus that the till gives back must not open the soft keyboard (see above).
        inputMode={touch && !typing ? 'none' : 'text'}
        onPointerDown={(event) => {
          if (!touch || typing) return;
          const input = event.currentTarget;
          flushSync(() => setTyping(true));
          // Already focused with inputmode=none: refocus so the keyboard opens for this tap.
          if (document.activeElement === input) {
            input.blur();
            window.requestAnimationFrame(() => input.focus());
          }
        }}
        onBlur={() => setTyping(false)}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          // IME composition (Tamil) commits with Enter: let it reach the field, not the lookup.
          const composing = event.nativeEvent.isComposing || event.keyCode === 229;
          if (composing) return;
          if (event.key === 'Enter') {
            event.preventDefault();
            void submit();
          }
        }}
      />
      {disabled ? null : (
        <span className="hidden items-center gap-1.5 text-xs font-semibold whitespace-nowrap xl:inline-flex" style={{ color: 'var(--ui-success)' }}>
          <span aria-hidden className="h-2 w-2 rounded-full" style={{ background: 'var(--ui-success-solid)' }} />
          {t('scan.ready')}
        </span>
      )}
      <Kbd className="hidden lg:inline-flex">F2</Kbd>
      <button
        type="button"
        data-testid="pos-barcode-add"
        className="btn btn-primary h-12 shrink-0 rounded-xl px-4 sm:px-5 short:h-9 short:min-h-9"
        disabled={disabled || looking || value.trim().length === 0}
        onClick={() => void submit()}
      >
        {looking ? <span className="spinner" aria-hidden /> : null}
        {t('scan.add')}
      </button>
    </div>
  );
}
